import React, { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { FormSection } from '@packages/ui/patterns';
import { Icon, Typography } from '@packages/ui/primitives';
import { palette, rem } from '@packages/ui/tokens';
import type { FormSectionNode } from '../../types/layout';
import type { Condition } from '../../types/conditions';
import { FormNodeRenderer } from '../FormNodeRenderer';
import { useFormEngineForm, useFormEngineState } from '../../context/FormEngineContext';
import { evaluateFieldState } from '../../processors/fieldStateEvaluator';
import { evaluateCondition, type ConditionContext } from '../../evaluators/conditionEvaluator';
import { computeReactionOverrides } from '../../processors/reactionProcessor';
import { prefixFieldConditions } from '../../utils/prefixFieldConditions';
import { collectSectionVisibilityFields } from '../../utils/collectLayoutFieldPaths';

interface SectionRendererProps {
  node: FormSectionNode;
  namePrefix?: string;
}

/** Legacy SingleDetailBlock header row — icon + section title on one line. */
const DetailSectionHeader = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  padding-bottom: ${rem(18)};
`;

const DetailSectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(10)};
  width: 100%;
`;

function resolveHeaderVariant(
  node: FormSectionNode,
): 'label' | 'heading' | 'h5' {
  return node.headerVariant ?? node.titleVariant ?? 'label';
}

export const FormSectionRenderer = React.memo(function FormSectionRenderer({
  node,
  namePrefix,
}: SectionRendererProps): React.ReactElement | null {
  const { form } = useFormEngineForm();
  const { meta, getOptionProp, isReadOnly, modeOverrides, blockOverrides } = useFormEngineState();
  const formValues = useWatch({ control: form.control });

  const sectionHidden = useMemo(() => {
    if (!node.visibleWhen) return false;

    const values = (formValues ?? form.getValues()) as Record<string, unknown>;
    const conditionCtx: ConditionContext = {
      values,
      meta,
      getOptionProp,
    };

    return !evaluateCondition(node.visibleWhen as unknown as Condition, conditionCtx);
  }, [node.visibleWhen, formValues, form, meta, getOptionProp]);

  const allChildrenHidden = useMemo(() => {
    if (sectionHidden) return true;

    const fields = collectSectionVisibilityFields(node.children);
    if (fields.length === 0) return false;

    const values = (formValues ?? form.getValues()) as Record<string, unknown>;

    for (const field of fields) {
      const prefixedField = prefixFieldConditions(field, namePrefix);
      const reactionOverrides = computeReactionOverrides({
        reactions: prefixedField.reactions,
        formValues: values,
        meta,
        getOptionProp,
        namePrefix,
      });

      const fieldState = evaluateFieldState(prefixedField, {
        formValues: values,
        meta,
        getOptionProp,
        isGlobalReadOnly: isReadOnly,
        modeOverrides,
        blockOverrides,
        reactionOverrides,
        namePrefix,
      });

      if (fieldState.visible) return false;
    }

    return true;
  }, [
    sectionHidden,
    node.children,
    formValues,
    form,
    meta,
    getOptionProp,
    isReadOnly,
    modeOverrides,
    blockOverrides,
    namePrefix,
  ]);

  if (allChildrenHidden) {
    return null;
  }

  const children = node.children.map((child, idx) => (
    <FormNodeRenderer key={idx} node={child} namePrefix={namePrefix} />
  ));

  const headerVariant = resolveHeaderVariant(node);
  const useDetailHeader =
    node.icon != null || headerVariant === 'heading' || headerVariant === 'h5';

  // Production participant tax detail: icon + title header (SingleDetailBlock),
  // then content below (empty "-" or account rows).
  if (useDetailHeader) {
    return (
      <div className={node.className} data-form-section={node.semanticId ?? node.title}>
        {(node.title != null && node.title !== '') && (
          <DetailSectionHeader>
            {node.icon != null && (
              <Icon
                name={node.icon}
                size='sm'
                color={palette.SpanishGray}
                style={{ marginLeft: rem(5), marginRight: rem(10), flexShrink: 0 }}
              />
            )}
            <Typography variant='h6' color={palette.Gray}>
              {node.title}
            </Typography>
          </DetailSectionHeader>
        )}
        {node.subtitle != null && node.subtitle !== '' && (
          <Typography variant='body-xs' color={palette.Gray} style={{ marginTop: rem(-12), marginBottom: rem(12) }}>
            {node.subtitle}
          </Typography>
        )}
        <DetailSectionBody>{children}</DetailSectionBody>
      </div>
    );
  }

  return (
    <FormSection
      title={node.title ?? ''}
      subtitle={node.subtitle}
      divider={node.divider !== false}
      titleVariant='label'
      semanticId={node.semanticId}
      className={node.className}
    >
      {children}
    </FormSection>
  );
});
